/**
 * News page year nav scroll spy + jump.
 * Mirrors about-nav.js; targets .c-news-year-marker anchors.
 * Collapsed nav shows first 5 years; "(...)" reveals older years.
 */

let cleanup = null;

function getScrollOffset() {
  const nav = document.querySelector(".js-news-years");
  if (!nav) {
    return 0;
  }

  const top = parseFloat(getComputedStyle(nav).top);
  return Number.isFinite(top) ? top : 0;
}

function getDocumentTop(el) {
  if (!el) {
    return Infinity;
  }

  return el.getBoundingClientRect().top + window.scrollY;
}

function expandOlderYears(nav) {
  if (!nav?.classList.contains("is-years-collapsed")) {
    return;
  }

  nav.classList.remove("is-years-collapsed");
  nav.dataset.yearsCollapsed = "false";

  const more = nav.querySelector(".js-news-years-more");
  if (more) {
    more.setAttribute("aria-expanded", "true");
  }
}

function setActiveLink(links, activeLink, nav) {
  links.forEach((link) => {
    const isActive = link === activeLink;
    link.classList.toggle("is-active", isActive);

    if (isActive) {
      link.setAttribute("aria-current", "true");
    } else {
      link.removeAttribute("aria-current");
    }
  });

  // If scroll-spy lands on a hidden older year, expand the list.
  if (activeLink?.classList.contains("c-news__year-link--older")) {
    expandOlderYears(nav);
  }
}

function scrollToYear(section) {
  const targetY = Math.max(0, getDocumentTop(section) - getScrollOffset());
  window.scrollTo({ top: targetY, left: 0, behavior: "auto" });
}

export function destroyNewsYears() {
  if (cleanup) {
    cleanup();
    cleanup = null;
  }
}

export function initNewsYears() {
  destroyNewsYears();

  const nav = document.querySelector(".js-news-years");
  if (!nav) {
    return;
  }

  const links = Array.from(nav.querySelectorAll(".c-news__year-link[href^='#']"));
  if (links.length === 0) {
    return;
  }

  const items = links
    .map((link) => {
      const id = decodeURIComponent(link.hash.slice(1));
      const section = id ? document.getElementById(id) : null;
      return section ? { link, section } : null;
    })
    .filter(Boolean);

  let ticking = false;

  const update = () => {
    ticking = false;

    if (items.length === 0) {
      return;
    }

    const marker = window.scrollY + getScrollOffset() + 1;
    const ordered = items
      .map((item) => ({ ...item, top: getDocumentTop(item.section) }))
      .sort((a, b) => a.top - b.top);

    let active = ordered[0];

    for (const item of ordered) {
      if (item.top <= marker) {
        active = item;
      }
    }

    const docBottom = window.scrollY + window.innerHeight;
    const pageBottom = document.documentElement.scrollHeight;
    if (pageBottom - docBottom < 4) {
      active = ordered[ordered.length - 1];
    }

    setActiveLink(
      items.map((item) => item.link),
      active.link,
      nav
    );
  };

  const onScrollOrResize = () => {
    if (ticking) {
      return;
    }
    ticking = true;
    requestAnimationFrame(update);
  };

  const onNavClick = (event) => {
    const more = event.target.closest(".js-news-years-more");
    if (more && nav.contains(more)) {
      event.preventDefault();
      expandOlderYears(nav);
      return;
    }

    const link = event.target.closest(".c-news__year-link[href^='#']");
    if (!link || !nav.contains(link)) {
      return;
    }

    const id = decodeURIComponent(link.hash.slice(1));
    const section = id ? document.getElementById(id) : null;
    if (!section) {
      return;
    }

    event.preventDefault();
    history.replaceState(null, "", `${window.location.pathname}${window.location.search}#${id}`);
    scrollToYear(section);
    setActiveLink(
      items.map((item) => item.link),
      link,
      nav
    );
  };

  window.addEventListener("scroll", onScrollOrResize, { passive: true });
  window.addEventListener("resize", onScrollOrResize);
  nav.addEventListener("click", onNavClick);

  if (items.length > 0) {
    update();
  }

  cleanup = () => {
    window.removeEventListener("scroll", onScrollOrResize);
    window.removeEventListener("resize", onScrollOrResize);
    nav.removeEventListener("click", onNavClick);
    setActiveLink(
      items.map((item) => item.link),
      null,
      nav
    );
  };
}
